/** Shared event-visit payload and scoring contract (T002-BE). */

import { isCalendarDay } from './returnVisits';

export const EVENT_VISITS_PROTOCOL_VERSION = 1;
export const EVENT_VISITS_PER_EVENT_DAILY_CAP = 300;
export const EVENT_VISITS_MIN_TOTAL = 10;
export const EVENT_VISITS_REF_TOTAL = 1000;
export const EVENT_VISITS_MAX_POINTS = 20;
export const EVENT_VISITS_MIN_VISIBLE_MS = 5000;
export const EVENT_VISITS_MAX_PAYLOAD_BYTES = 512;

export type EventVisitContribution = {
  version: 1;
  eventId: string;
  day: string;
};

const UUID_RE = /^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/;
const CONTRIBUTION_KEYS = ['version', 'eventId', 'day'] as const;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Strictly decodes an event visit POST payload without exposing invalid data. */
export function decodeEventVisitContribution(value: unknown): EventVisitContribution | null {
  if (!isPlainObject(value)) return null;
  const actualKeys = Object.keys(value);
  if (
    actualKeys.length !== CONTRIBUTION_KEYS.length ||
    !CONTRIBUTION_KEYS.every((key) => key in value)
  )
    return null;
  if (value.version !== EVENT_VISITS_PROTOCOL_VERSION) return null;
  if (typeof value.eventId !== 'string' || !UUID_RE.test(value.eventId)) return null;
  if (!isCalendarDay(value.day)) return null;
  return { version: 1, eventId: value.eventId, day: value.day };
}

/** Maps cumulative visit totals to the bounded 0–20 popularity component. */
export function visitScore(total: number): number {
  if (!Number.isFinite(total) || total < EVENT_VISITS_MIN_TOTAL) return 0;
  const normalizedLog =
    Math.log10(total / EVENT_VISITS_MIN_TOTAL) /
    Math.log10(EVENT_VISITS_REF_TOTAL / EVENT_VISITS_MIN_TOTAL);
  return Math.round(EVENT_VISITS_MAX_POINTS * Math.min(1, normalizedLog));
}
